import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CalendarDays, ArrowRight, Ticket, GraduationCap, Award, Users, CheckCircle2 } from 'lucide-react';
import { calendarAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { canCreateEvents, isKRRole } from '@/lib/utils';
import { formatEventDateTime } from '@/lib/eventTime';

function fmtDate(iso, tz) {
  return formatEventDateTime(iso, tz);
}

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso); const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};
const withinDays = (iso, days) => {
  if (!iso) return false;
  const d = new Date(iso); const now = new Date();
  return d >= now && (d - now) / (1000 * 60 * 60 * 24) <= days;
};

function EventCard({ ev, onOpen }) {
  return (
    <div onClick={() => onOpen(ev)}
         className="rounded-md border border-slate-200 bg-white p-3 hover:border-slate-400 cursor-pointer transition"
         data-testid={`dash-event-${ev.event_id}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{fmtDate(ev.start_datetime, ev.timezone)}</div>
      <div className="font-medium text-slate-800">{ev.title}</div>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        <Badge variant="outline" className="text-[10px]">{(ev.visibility_scope || '').replace(/_/g, ' ')}</Badge>
        {ev.event_type && <Badge variant="outline" className="text-[10px]">{ev.event_type.replace(/_/g, ' ')}</Badge>}
        {ev.business_category && <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-200 text-blue-800">{ev.business_category.replace(/_/g, ' ')}</Badge>}
        {ev.language && <Badge variant="outline" className="text-[10px] bg-violet-50 border-violet-200 text-violet-700 capitalize" data-testid={`dash-language-${ev.event_id}`}>{ev.language}</Badge>}
      </div>
    </div>
  );
}

function CardBlock({ icon: Icon, title, count, events, empty, link, onOpen, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500" /> {title}
          <span className="text-xs text-slate-500 font-normal">({count})</span>
        </CardTitle>
        {link && (
          <Button size="sm" variant="ghost" onClick={link.onClick} className="text-xs">
            View <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {events.length === 0 ? (
          <div className="text-xs text-slate-500 italic py-2">{empty}</div>
        ) : (
          events.slice(0, 4).map((ev) => <EventCard key={ev.event_id} ev={ev} onOpen={onOpen} />)
        )}
      </CardContent>
    </Card>
  );
}

export default function EventsCalendarDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = canCreateEvents(user);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await calendarAPI.listEvents({ limit: 200 });
      setEvents(data.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEvent = (ev) => navigate(`/portal/events/${ev.event_id}`);
  const goList = (params = {}) => {
    const q = new URLSearchParams(params).toString();
    navigate(`/events${q ? `?${q}` : ''}`);
  };

  const published = events.filter((e) => e.status === 'published');
  const myUpcoming = published.filter((e) => (e.start_datetime || '') >= new Date().toISOString());
  const today = published.filter((e) => isToday(e.start_datetime));
  const thisWeek = published.filter((e) => withinDays(e.start_datetime, 7));
  const trainings = published.filter((e) => e.event_type === 'training' && withinDays(e.start_datetime, 30));
  const certs = published.filter((e) => e.event_type === 'certification' && withinDays(e.start_datetime, 30));
  const completed = events.filter((e) => e.status === 'completed').slice(0, 5);

  return (
    <div className="p-6 space-y-6" data-testid="events-dashboard">
      <PageHeader
        title="Events & Calendar"
        description={`${thisWeek.length} Events This Week · ${myUpcoming.length} Upcoming`}
        breadcrumbs={[{ label: 'Events & Calendar' }]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/calendar')}>
              <CalendarDays className="h-4 w-4 mr-2" /> Calendar
            </Button>
            <Button variant="outline" onClick={() => navigate('/events')}>
              <Ticket className="h-4 w-4 mr-2" /> Events
            </Button>
            {canCreate && <Button onClick={() => navigate('/calendar?create=1')}>Create Event</Button>}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <CardBlock icon={CalendarDays} title="Today's Events" count={today.length} events={today}
                   empty="No events today." onOpen={openEvent} testId="card-today"
                   link={{ onClick: () => navigate('/calendar') }} />
        <CardBlock icon={CalendarDays} title="Upcoming This Week" count={thisWeek.length} events={thisWeek}
                   empty="Nothing scheduled this week." onOpen={openEvent} testId="card-week"
                   link={{ onClick: () => goList({ status: 'published' }) }} />
        <CardBlock icon={Users} title="My Upcoming Events" count={myUpcoming.length} events={myUpcoming}
                   empty="You have no upcoming events." onOpen={openEvent} testId="card-my-upcoming"
                   link={{ onClick: () => goList({ status: 'published' }) }} />
        <CardBlock icon={GraduationCap} title="Upcoming Trainings" count={trainings.length} events={trainings}
                   empty="No trainings in the next 30 days." onOpen={openEvent} testId="card-trainings"
                   link={{ onClick: () => goList({ type: 'training', status: 'published' }) }} />
        <CardBlock icon={Award} title="Upcoming Certifications" count={certs.length} events={certs}
                   empty="No certifications in the next 30 days." onOpen={openEvent} testId="card-certifications"
                   link={{ onClick: () => goList({ type: 'certification', status: 'published' }) }} />
        <CardBlock icon={CheckCircle2} title="Recently Completed" count={completed.length} events={completed}
                   empty="No completed events yet." onOpen={openEvent} testId="card-completed"
                   link={{ onClick: () => goList({ status: 'completed' }) }} />
      </div>
    </div>
  );
}
